import React, { useState, useMemo, useEffect } from "react";
import { useStaticQuery, graphql, navigate } from "gatsby";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";
import rgba from "polished/lib/color/rgba";
import type { InsightStructuredDocument } from "~/molecules/InsightRichTextBody";

type Article = {
  id: string;
  contentful_id: string;
  title: string;
  path?: string;
  publishedAt?: string;
  body?: {
    childMarkdownRemark?: {
      rawMarkdownBody?: string;
    };
  };
  structuredBody?: InsightStructuredDocument | null;
  category?: Array<{
    id?: string;
    contentful_id?: string;
    name?: string;
    slug?: string;
  }>;
  cover?: {
    id?: string;
    contentful_id?: string;
    fluid?: {
      base64?: string;
      aspectRatio?: number;
      src?: string;
      srcSet?: string;
      sizes?: string;
    };
  };
};

type Props = {
  onSearchResultsChange: (articles: Article[], isSearching: boolean, query?: string) => void;
  location: Location;
  initialQuery?: string;
};

export const InsightSearchBar: React.FC<Props> = ({ onSearchResultsChange, location, initialQuery = "" }) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [isPc, setIsPc] = useState(false);

  const { allArticles } = useStaticQuery<GatsbyTypes.InsightSearchArticlesQuery>(graphql`
    query InsightSearchArticles {
      allArticles: allContentfulInsightArticle(sort: { fields: publishedAt, order: DESC }) {
        edges {
          node {
            id
            contentful_id
            title
            path
            publishedAt
            body {
              childMarkdownRemark {
                rawMarkdownBody
              }
            }
            structuredBody {
              plainText
            }
            category {
              id
              contentful_id
              name
              slug
            }
            cover {
              id
              contentful_id
              fluid(maxWidth: 660, quality: 90) {
                base64
                aspectRatio
                src
                srcSet
                sizes
              }
            }
          }
        }
      }
    }
  `) as { allArticles: { edges: Array<{ node: Article }> } };

  const allArticlesList = useMemo(() => allArticles.edges.map((edge: any) => edge.node), [allArticles]);

  // URLのクエリパラメータを更新
  const updateUrl = (query: string) => {
    const trimmedQuery = query.trim();
    const searchParams = new URLSearchParams(location.search);

    if (trimmedQuery === "") {
      searchParams.delete("q");
    } else {
      searchParams.set("q", trimmedQuery);
    }

    const newSearch = searchParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${location.hash || ""}`;

    navigate(newUrl, { replace: true });
  };

  const performSearch = (query: string) => {
    const trimmedQuery = query.trim().toLowerCase();
    const originalQuery = query.trim();

    if (trimmedQuery === "") {
      onSearchResultsChange([], false, "");
      return;
    }

    // タイトルに検索ワードを含む記事を抽出
    const titleMatched = allArticlesList.filter((article: Article) => {
      return article.title?.toLowerCase().includes(trimmedQuery);
    });

    // 本文に検索ワードを含む記事を抽出（タイトルにはマッチしないもの）
    const bodyMatched = allArticlesList.filter((article: Article) => {
      const titleMatch = article.title?.toLowerCase().includes(trimmedQuery);
      if (titleMatch) return false; // タイトルにマッチするものは除外
      const bodyText =
        article.structuredBody?.plainText?.toLowerCase() || article.body?.childMarkdownRemark?.rawMarkdownBody?.toLowerCase() || "";
      return bodyText.includes(trimmedQuery);
    });

    // 日付の新しい順でソート（publishedAtが新しい順）
    const sortByDate = (a: Article, b: Article) => {
      const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return dateB - dateA; // 降順（新しい順）
    };

    const sortedTitleMatched = titleMatched.sort(sortByDate);
    const sortedBodyMatched = bodyMatched.sort(sortByDate);

    // タイトルマッチ → 本文マッチの順で結合
    const filtered = [...sortedTitleMatched, ...sortedBodyMatched];

    onSearchResultsChange(filtered, true, originalQuery);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    updateUrl(query);
    performSearch(query);
  };

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    // PCの場合は入力中に検索を実行しない
    if (!isPc) {
      handleSearch(value);
    }
  };

  const handleSearchClick = () => {
    handleSearch(searchQuery);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch(searchQuery);
    }
  };

  // PC/SP判定
  useEffect(() => {
    const checkIsPc = () => {
      if (typeof window === "undefined") return;
      setIsPc(window.innerWidth >= 768);
    };

    checkIsPc();
    window.addEventListener("resize", checkIsPc);
    return () => window.removeEventListener("resize", checkIsPc);
  }, []);

  // 初期化時にクエリパラメータから検索を実行
  useEffect(() => {
    if (initialQuery) {
      performSearch(initialQuery);
    }
  }, []); // 初回のみ実行

  return (
    <>
      <style jsx>{`
        .searchWrapper {
          width: 100%;
          margin-bottom: 30px;
        }
        .searchInput {
          font-size: 16px;
          line-height: 1.5;
          border: 1px solid ${colors.gray3};
          background-color: ${colors.gray3};
          border-radius: 8px;
          padding: 10px 40px 10px 15px;
          height: 44px;
          width: 100%;
          box-sizing: border-box;
          display: block;
          box-shadow: none;
          transition: all 0.15s ease-out;
        }
        .searchInput::placeholder {
          color: ${colors.gray2};
        }
        .searchInput:focus {
          outline: 0;
          border: 1px solid ${colors.orange};
          box-shadow: 0 0 3px 3px ${rgba(colors.orange, 0.1)};
          background-color: ${colors.white};
        }
        .searchContainer {
          position: relative;
          width: 100%;
        }
        .searchIcon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          color: ${colors.gray2};
        }
        .searchIcon.clickable {
          cursor: pointer;
          pointer-events: auto;
        }
        .searchIcon:not(.clickable) {
          pointer-events: none;
        }
        .searchIcon svg {
          width: 100%;
          height: 100%;
        }
        @media (${breakPoints.sp}) {
          .searchWrapper {
            margin-bottom: 20px;
          }
        }
      `}</style>
      <div className="searchWrapper">
        <div className="searchContainer">
          <input
            type="text"
            className="searchInput"
            placeholder="キーワードで記事を検索..."
            value={searchQuery}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className={`searchIcon ${isPc ? "clickable" : ""}`} onClick={isPc ? handleSearchClick : undefined}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>
    </>
  );
};
